import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  AlertTriangle, Building2, X, Calendar, Clock, FileText, Check,
  Plus, Upload, Image as ImageIcon, Save, Send, Gavel, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { key: 'non_respect_dates',     label: 'Non-respect des dates de diffusion' },
  { key: 'panneau_hs',            label: 'Le panneau ne fonctionne pas (éteint, en panne)' },
  { key: 'retard_pose',           label: 'Retard de pose de l\'affiche' },
  { key: 'facturation_contestee', label: 'Facturation ou paiement contesté' },
  { key: 'probleme_affichage',    label: "Problème d'affichage (visuel abîmé, flou, mal posé)" },
  { key: 'affiche_enlevee',       label: 'Affiche enlevée avant le terme du contrat' },
  { key: 'mauvais_emplacement',   label: 'Mauvais emplacement ou non conforme' },
  { key: 'autre',                 label: 'Autre (préciser)' },
];

const DEFAULT_DEMARCHES = [
  { key: 'constater',      titre: 'Constater et documenter',  subtitle: 'Photos datées, contrat et réservation joints au dossier.', done: false, date_action: '', deadline: '' },
  { key: 'contacter',      titre: "Contacter l'entreprise",   subtitle: 'Appel puis e-mail avec le dossier complet.',                 done: false, date_action: '', deadline: '' },
  { key: 'mise_en_demeure',titre: 'Mise en demeure',          subtitle: 'Lettre recommandée, délai de réponse de 8 jours.',          done: false, date_action: '', deadline: '' },
  { key: 'rdv_mediation',  titre: 'Rendez-vous de médiation', subtitle: 'Échange avec le responsable litiges.',                       done: false, date_action: '', deadline: '' },
  { key: 'resolution',     titre: 'Résolution',               subtitle: 'Réaffichage, avoir, remboursement ou recours.',              done: false, date_action: '', deadline: '' },
];

const SOLUTIONS = ['Réaffichage', 'Avoir', 'Remboursement', 'Prolongation'];
const GRAVITES = [
  { k: 'faible',  label: 'Faible',  color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { k: 'moyenne', label: 'Moyenne', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { k: 'elevee',  label: 'Élevée',  color: 'bg-rose-100 text-rose-700 border-rose-300' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function LitigeFormSheet({ open, onClose, onSaved, contrats = [], initial = null }) {
  const [savedId, setSavedId] = useState(null);  // id du dossier (après 1er enregistrement = pour les uploads)
  const [savedNumero, setSavedNumero] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pieces, setPieces] = useState([]);
  const [form, setForm] = useState({
    entreprise: '', contact_nom: '', contact_telephone: '', contact_email: '',
    numero_contrat: '', contrat_id: '',
    type_contrat: '', date_debut: '', date_fin: '', emplacement_panneau: '',
    types: [],
    date_litige: todayStr(), date_constat: todayStr(),
    gravite: 'moyenne', description: '',
    solutions_souhaitees: [], prejudice_estime: '',
    demarches: DEFAULT_DEMARCHES.map(d => ({ ...d })),
    rdv_date: '', rdv_heure: '10:00', rdv_type: 'visio',
    rdv_participants: '', rdv_rappel_email: true, rdv_rappel_sms: false,
    statut: 'brouillon',
  });

  // Reset / initial load
  useEffect(() => {
    if (open) {
      if (initial?.id) {
        setForm({ ...form, ...initial });
        setPieces(initial.pieces || []);
        setSavedId(initial.id);
        setSavedNumero(initial.numero || '');
      } else {
        setForm({
          entreprise: '', contact_nom: '', contact_telephone: '', contact_email: '',
          numero_contrat: '', contrat_id: '',
          type_contrat: '', date_debut: '', date_fin: '', emplacement_panneau: '',
          types: [],
          date_litige: todayStr(), date_constat: todayStr(),
          gravite: 'moyenne', description: '',
          solutions_souhaitees: [], prejudice_estime: '',
          demarches: DEFAULT_DEMARCHES.map(d => ({ ...d })),
          rdv_date: '', rdv_heure: '10:00', rdv_type: 'visio',
          rdv_participants: '', rdv_rappel_email: true, rdv_rappel_sms: false,
          statut: 'brouillon',
          ...(initial || {}),
        });
        setPieces([]);
        setSavedId(null);
        setSavedNumero('');
      }
    }
    // eslint-disable-next-line
  }, [open, initial]);

  if (!open) return null;

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleType = (k) => setField('types', form.types.includes(k) ? form.types.filter(x => x !== k) : [...form.types, k]);
  const toggleSolution = (s) => setField('solutions_souhaitees', form.solutions_souhaitees.includes(s) ? form.solutions_souhaitees.filter(x => x !== s) : [...form.solutions_souhaitees, s]);

  // Auto-remplir depuis contrat
  const applyContrat = (contrat_id) => {
    const c = contrats.find(x => x.id === contrat_id);
    if (!c) return;
    setForm(f => ({
      ...f,
      entreprise: c.entreprise || f.entreprise,
      contact_nom: c.contact_nom || c.nom_contact || f.contact_nom,
      contact_telephone: c.telephone || c.contact_telephone || f.contact_telephone,
      contact_email: c.email || c.contact_email || f.contact_email,
      numero_contrat: c.numero || c.reference || f.numero_contrat,
      contrat_id,
      type_contrat: c.type_contrat || c.duree_type || f.type_contrat,
      date_debut: c.date_debut || f.date_debut,
      date_fin: c.date_fin || f.date_fin,
      emplacement_panneau: c.emplacement || f.emplacement_panneau,
    }));
  };

  const saveDossier = async (nextStatut = null) => {
    setSaving(true);
    try {
      const body = {
        ...form,
        prejudice_estime: parseFloat(form.prejudice_estime || 0) || 0,
        statut: nextStatut || form.statut,
      };
      let res;
      if (savedId) {
        res = await axios.put(`${API}/api/litiges/${savedId}`, body);
      } else {
        res = await axios.post(`${API}/api/litiges`, body);
        setSavedId(res.data.id);
        setSavedNumero(res.data.numero);
      }
      toast.success(nextStatut === 'ouvert' ? 'Litige ouvert ✓'
        : nextStatut === 'mise_en_demeure' ? 'Mise en demeure envoyée'
        : 'Brouillon enregistré');
      if (nextStatut === 'ouvert') onSaved?.();
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const uploadPieces = async (files) => {
    if (!files?.length) return;
    // S'assurer qu'un ID existe (sinon on enregistre d'abord un brouillon)
    let litId = savedId;
    if (!litId) {
      const saved = await saveDossier();
      litId = saved?.id;
      if (!litId) return;
    }
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/api/litiges/${litId}/pieces`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPieces(prev => [...prev, r.data]);
      }
      toast.success('Pièce(s) ajoutée(s)');
    } catch (e) {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const deletePiece = async (index) => {
    if (!savedId) return;
    try {
      await axios.delete(`${API}/api/litiges/${savedId}/pieces/${index}`);
      setPieces(prev => prev.filter((_, i) => i !== index));
    } catch { toast.error('Erreur suppression'); }
  };

  const toggleDemarche = (idx) => {
    setForm(f => {
      const arr = [...f.demarches];
      arr[idx] = { ...arr[idx], done: !arr[idx].done, date_action: !arr[idx].done ? todayStr() : '' };
      return { ...f, demarches: arr };
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto" data-testid="litige-form-panel">
      <div className="min-h-full flex items-start justify-center p-0 sm:p-4">
        <div className="w-full max-w-4xl bg-slate-50 rounded-none sm:rounded-2xl shadow-2xl my-0 sm:my-4 overflow-hidden">
          {/* En-tête vert */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-6 py-5 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
                <Gavel className="h-6 w-6" /> Module litige
              </h1>
              <p className="text-slate-200 text-xs mt-1">Dossier structuré pour contestation client</p>
            </div>
            <div className="text-right text-xs text-slate-200">
              <p className="font-bold text-white text-sm">{savedNumero || 'Dossier LIT-xxxx-xxxx (en création)'}</p>
              <p>Statut : <b className="text-white">{form.statut}</b></p>
              <p>Ouvert le {new Date().toLocaleDateString('fr-FR')}</p>
              <button
                onClick={onClose}
                className="mt-2 inline-flex items-center gap-1 text-xs text-white/80 hover:text-white"
                data-testid="btn-close-litige"
              >
                <X className="h-4 w-4" /> Fermer
              </button>
            </div>
          </div>

          <div className="p-5 space-y-5">

            {/* 1 — Infos entreprise */}
            <Section n={1} title="Informations de l'entreprise">
              {contrats.length > 0 && (
                <div className="mb-3">
                  <Label className="text-xs">Lier à un contrat existant</Label>
                  <select
                    className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
                    value={form.contrat_id}
                    onChange={(e) => applyContrat(e.target.value)}
                    data-testid="select-contrat-litige"
                  >
                    <option value="">— Choisir un contrat (pré-remplit les champs) —</option>
                    {contrats.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.entreprise} — {c.numero || c.reference || c.id.slice(0, 8)} · {c.date_debut || '?'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Entreprise" value={form.entreprise} onChange={(v) => setField('entreprise', v)} />
                <Field label="Contact" value={form.contact_nom} onChange={(v) => setField('contact_nom', v)} />
                <Field label="N° de contrat" value={form.numero_contrat} onChange={(v) => setField('numero_contrat', v)} />
                <Field label="Type de contrat" value={form.type_contrat} onChange={(v) => setField('type_contrat', v)} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Début" type="date" value={form.date_debut} onChange={(v) => setField('date_debut', v)} />
                  <Field label="Fin" type="date" value={form.date_fin} onChange={(v) => setField('date_fin', v)} />
                </div>
                <Field label="Emplacement du panneau" value={form.emplacement_panneau} onChange={(v) => setField('emplacement_panneau', v)} />
                <Field label="Téléphone contact" value={form.contact_telephone} onChange={(v) => setField('contact_telephone', v)} />
                <Field label="Email contact" value={form.contact_email} onChange={(v) => setField('contact_email', v)} />
              </div>
            </Section>

            {/* 2 — Types de litige */}
            <Section n={2} title="Type de litige" right={<span className="text-xs text-slate-500">Cocher tous les cas concernés</span>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {TYPES.map(t => (
                  <label
                    key={t.key}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${
                      form.types.includes(t.key)
                        ? 'bg-slate-100 border-slate-400'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`type-${t.key}`}
                  >
                    <Checkbox
                      checked={form.types.includes(t.key)}
                      onCheckedChange={() => toggleType(t.key)}
                      className="data-[state=checked]:bg-slate-600 data-[state=checked]:border-slate-700"
                    />
                    <span className="text-sm text-slate-800">{t.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {form.types.length} cas coché{form.types.length > 1 ? 's' : ''}
                {' · '}chaque case ajoute le cas au dossier
              </p>
            </Section>

            {/* 3 — Détails */}
            <Section n={3} title="Détails du litige">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <Field label="Date du litige" type="date" value={form.date_litige} onChange={(v) => setField('date_litige', v)} />
                <Field label="Date du constat" type="date" value={form.date_constat} onChange={(v) => setField('date_constat', v)} />
                <div>
                  <Label className="text-xs text-slate-500">Gravité</Label>
                  <div className="flex gap-2 mt-1">
                    {GRAVITES.map(g => (
                      <button
                        key={g.k}
                        type="button"
                        onClick={() => setField('gravite', g.k)}
                        className={`flex-1 text-xs font-semibold py-2 rounded-md border-2 transition ${
                          form.gravite === g.k ? g.color : 'bg-white text-slate-500 border-slate-200'
                        }`}
                        data-testid={`gravite-${g.k}`}
                      >{g.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Description des faits</Label>
                  <Textarea
                    rows={5}
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    placeholder="Ex. Affiche retirée le 01/10 alors que le contrat court jusqu'au 14/01/2027…"
                    data-testid="input-description-litige"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Solution souhaitée</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {SOLUTIONS.map(s => (
                      <label key={s} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                        form.solutions_souhaitees.includes(s) ? 'bg-slate-100 border-slate-400 text-slate-800' : 'bg-white border-slate-200 text-slate-700'
                      }`}>
                        <Checkbox
                          checked={form.solutions_souhaitees.includes(s)}
                          onCheckedChange={() => toggleSolution(s)}
                          className="data-[state=checked]:bg-slate-600 data-[state=checked]:border-slate-700"
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500">Préjudice estimé (FCFA)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.prejudice_estime}
                      onChange={(e) => setField('prejudice_estime', e.target.value)}
                      placeholder="Ex: 1 200 000"
                    />
                  </div>
                </div>
              </div>
            </Section>

            {/* 4 — Pièces jointes */}
            <Section n={4} title="Pièces jointes" right={<span className="text-xs text-slate-500">Contrats, réservations, photos, e-mails</span>}>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                {pieces.map((p, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-white">
                    <div className="aspect-square flex items-center justify-center">
                      {(p.content_type || '').startsWith('image/') ? (
                        <AuthenticatedImage
                          src={`${API}/api/litiges/${savedId}/pieces/${i}/file`}
                          alt={p.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-slate-400 gap-1">
                          <FileText className="h-10 w-10" />
                          <span className="text-[10px] px-2 truncate max-w-full">{p.filename}</span>
                        </div>
                      )}
                    </div>
                    <div className="px-2 py-1 text-[10px] text-center text-slate-600 bg-slate-50 border-t border-slate-100 truncate">{p.label || p.filename}</div>
                    <button
                      onClick={() => deletePiece(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      data-testid={`btn-delete-piece-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-slate-400 flex flex-col items-center justify-center gap-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  data-testid="btn-add-piece"
                >
                  <Plus className="h-8 w-8" />
                  <span className="text-xs font-semibold">{uploading ? 'Envoi…' : 'Ajouter une image'}</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  hidden
                  onChange={(e) => { uploadPieces(e.target.files); e.target.value = ''; }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Formats acceptés : JPG, PNG, PDF · Toucher une image pour l'agrandir · Croix rouge pour supprimer
              </p>
            </Section>

            {/* 5 — Démarches à suivre */}
            <Section n={5} title="Démarches à suivre" right={<span className="text-xs text-slate-500">Cocher chaque étape terminée</span>}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 relative">
                {form.demarches.map((d, i) => (
                  <div key={d.key} className="flex flex-col items-center text-center relative">
                    <button
                      onClick={() => toggleDemarche(i)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition ${
                        d.done ? 'bg-slate-600 text-white' : 'bg-white border-2 border-slate-300 text-slate-500'
                      }`}
                      data-testid={`demarche-${i}`}
                    >
                      {d.done ? <Check className="h-5 w-5" /> : i + 1}
                    </button>
                    {i < form.demarches.length - 1 && (
                      <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-slate-200 -z-10" style={{ transform: 'translateX(50%)' }}></div>
                    )}
                    <p className="text-xs font-bold text-slate-800 mt-2">{d.titre}</p>
                    <p className="text-[10px] text-slate-500 mt-1 leading-tight">{d.subtitle}</p>
                    <p className="text-[10px] mt-2 px-2 py-0.5 rounded-full border font-semibold inline-block">
                      {d.done ? (
                        <span className="text-slate-700 border-slate-400 bg-slate-100">Fait le {new Date(d.date_action).toLocaleDateString('fr-FR')}</span>
                      ) : (
                        <span className="text-slate-500 border-slate-200">À faire</span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            {/* 6 — RDV */}
            <Section n={6} title="Planifier un rendez-vous">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Date" type="date" value={form.rdv_date} onChange={(v) => setField('rdv_date', v)} />
                <Field label="Heure" type="time" value={form.rdv_heure} onChange={(v) => setField('rdv_heure', v)} />
                <div>
                  <Label className="text-xs text-slate-500">Type de rendez-vous</Label>
                  <div className="flex gap-2 mt-1">
                    {['visio', 'sur_place', 'telephone'].map(t => (
                      <button
                        key={t}
                        onClick={() => setField('rdv_type', t)}
                        className={`flex-1 text-xs font-semibold py-2 rounded-md border transition ${
                          form.rdv_type === t ? 'bg-slate-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200'
                        }`}
                        data-testid={`rdv-type-${t}`}
                      >
                        {t === 'visio' ? 'Visio' : t === 'sur_place' ? 'Sur place' : 'Téléphone'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-slate-500">Participants</Label>
                <Input
                  value={form.rdv_participants}
                  onChange={(e) => setField('rdv_participants', e.target.value)}
                  placeholder="Ex: Paul Martin (entreprise) · Responsable litiges · Moi"
                />
              </div>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.rdv_rappel_email}
                    onCheckedChange={(v) => setField('rdv_rappel_email', !!v)}
                    className="data-[state=checked]:bg-slate-600 data-[state=checked]:border-slate-700"
                  />
                  Rappel e-mail 24 h avant
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.rdv_rappel_sms}
                    onCheckedChange={(v) => setField('rdv_rappel_sms', !!v)}
                    className="data-[state=checked]:bg-slate-600 data-[state=checked]:border-slate-700"
                  />
                  Rappel SMS 1 h avant
                </label>
              </div>
            </Section>

          </div>

          {/* Actions bas */}
          <div className="bg-white border-t border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-2 sticky bottom-0">
            <Button
              variant="outline"
              onClick={() => saveDossier('brouillon')}
              disabled={saving}
              className="gap-2"
              data-testid="btn-save-brouillon"
            >
              <Save className="h-4 w-4" /> Enregistrer le brouillon
            </Button>
            <Button
              variant="outline"
              onClick={() => saveDossier('mise_en_demeure')}
              disabled={saving || !form.entreprise}
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="btn-mise-demeure"
            >
              <Send className="h-4 w-4" /> Envoyer la mise en demeure
            </Button>
            <Button
              onClick={() => saveDossier('ouvert')}
              disabled={saving || !form.entreprise || form.types.length === 0}
              className="bg-slate-700 hover:bg-slate-800 gap-2"
              data-testid="btn-ouvrir-litige"
            >
              <Gavel className="h-4 w-4" /> Ouvrir le litige
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, right, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
          <span className="w-7 h-7 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold">{n}</span>
          {title}
        </h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function AuthenticatedImage({ src, alt, className }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(src, { responseType: 'blob' });
        if (cancelled) return;
        setBlobUrl(URL.createObjectURL(res.data));
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [src]);
  if (!blobUrl) return <div className={`${className} bg-slate-100 flex items-center justify-center text-slate-400`}><ImageIcon className="h-6 w-6" /></div>;
  return <img src={blobUrl} alt={alt} className={className} />;
}
